import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import {put} from 'redux-saga/effects';
import {events} from '../constant/talkViewerEvents';
import {TalkViewerState} from '../state/TalkViewerState.interface';
import {
  appendTalk,
  changeScene,
  changeTalkGroup,
  hideTalkReplayBox,
} from '../state/talkViewerSlice';

/**
 * 현재 상태에서 한 단계 다음으로 진행한다.
 * 톡 -> 다음톡, 톡그룹 -> 다음 톡그룹 등
 * 다음 톡을 진행하게 되면 진행한 톡 id를 반환하고, 진행하지 못하면 undefined return
 */
export function* processNextStep(talkViewerState: TalkViewerState) {
  const {
    productInfoMap,
    currentProductId,
    talkMap,
    talkGroupMap,
    sceneMap,
    sceneInfoMap,
    shownReplay,
  } = talkViewerState;
  if (shownReplay) {
    yield put(hideTalkReplayBox());
  }
  if (!currentProductId) {
    return;
  }
  const currentProduct = productInfoMap[currentProductId];
  const {currentSceneId, sceneIds} = currentProduct;
  if (!currentSceneId) {
    return;
  }
  const currentScene = sceneInfoMap[currentSceneId];
  if (!currentScene) {
    return;
  }
  const {talkHistory, talkGroupId} = currentScene;
  const scene = sceneMap[currentScene.sceneId];
  if (!scene) {
    return;
  }
  const currentTalkGroup = talkGroupMap[talkGroupId];
  const {talkIds: currentTalkGroupTalks} = currentTalkGroup;

  // 씬에 진행된 톡 히스토리가 없고 톡그룹에 톡이 있다면 다음 톡 진행하고 리턴
  if (isEmpty(talkHistory) && !isEmpty(currentTalkGroupTalks)) {
    yield put(appendTalk(currentTalkGroupTalks[0]));
    return currentTalkGroupTalks[0];
  }

  // 현재 톡 그룹에 더 진행할 톡이 있다면 다음 톡 진행하고 리턴
  const lastTalkId = last(talkHistory);
  if (lastTalkId) {
    const talk = talkMap[lastTalkId];
    if (!talk) {
      return;
    }
    const indexTalkIdOfTalkGroup = indexOf(currentTalkGroupTalks, lastTalkId);
    if (
      indexTalkIdOfTalkGroup > -1 &&
      indexTalkIdOfTalkGroup < currentTalkGroupTalks.length - 1
    ) {
      yield put(appendTalk(currentTalkGroupTalks[indexTalkIdOfTalkGroup + 1]));
      return currentTalkGroupTalks[indexTalkIdOfTalkGroup + 1];
    }
  }

  // 씬의 마지막 톡그룹이 아니면 다음 톡그룹으로 변경하고 리턴
  const {talkGroupIds} = scene;
  const indexTalkGroupIdOfScene = indexOf(talkGroupIds, talkGroupId);
  if (indexTalkGroupIdOfScene < talkGroupIds.length - 1) {
    const nextTalkGroupId = talkGroupIds[indexTalkGroupIdOfScene + 1];

    yield put(changeTalkGroup(nextTalkGroupId));

    const {talkIds: nextTalkGroupTalkIds} = talkGroupMap[nextTalkGroupId];

    // 다음 톡그룹으로 진행했을 때, 톡이 존재하면 첫 톡을 내보낸다
    if (!isEmpty(nextTalkGroupTalkIds)) {
      yield put(appendTalk(nextTalkGroupTalkIds[0]));
      return nextTalkGroupTalkIds[0];
    }
    return;
  }
  // 만약 현재 씬이 마지막 씬이었다면 바로 리턴한다
  const isLastScene = currentScene.sceneId === last(sceneIds);
  if (isLastScene) {
    yield put(events.endOfTalkReached());
    return;
  }

  // 위 조건들에 해당하지 않으면 다음 씬으로 넘긴다.
  const toBeChangedSceneId =
    sceneIds[indexOf(sceneIds, currentScene.sceneId) + 1];
  const toBeChangedScene = sceneMap[toBeChangedSceneId];

  yield put(changeScene(toBeChangedSceneId));

  yield put(changeTalkGroup(toBeChangedScene.talkGroupIds[0]));

  const {talkIds: nextTalkGroupTalkIds} =
    talkGroupMap[toBeChangedScene.talkGroupIds[0]];

  // 다음 톡그룹으로 진행했을 때, 톡이 존재하면 첫 톡을 내보낸다
  if (!isEmpty(nextTalkGroupTalkIds)) {
    yield put(appendTalk(nextTalkGroupTalkIds[0]));
    return nextTalkGroupTalkIds[0];
  }

  return;
}
